import React, { useId, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

// TRANSLATIONS
import { useTranslation } from 'react-i18next';

// COMPONENTS
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Underline from '@editorjs/underline';
import Quote from '@editorjs/quote';
import ImageTool from './EditorTools/Image/Index';
import SocialEmbed from './EditorTools/SocialEmbed';
import Label from './Label';
import Error from './Error';
import Field from './Field';
import useEditorJs from './EditorTools/Hooks/UseEditorJs';
import translation from '../../../Translations/editor';

// STYLES
import formStyle from '../../../../scss/components/form.module.scss';
import style from '../../../../scss/components/editor.module.scss';
import '../../../../scss/external/editor-image-tool.scss';
import '../../../../scss/external/editor.scss';
import InlineLink from './EditorTools/InlineLink';
import decodeArticleOutput from '../../../Helpers/article/decodeArticleOutput';

const BlockStyledEditor = ({
    entity,
    name,
    fallbackName,
    resource,
    onChange,
    errors,
    fullFeatureSet = false,
    ...fieldProps
}) => {
    const { t } = useTranslation();
    const reactId = useId();

    const holderId = useMemo(() => {
        return `editor-holder-${resource}-${name}-${reactId.replace(
            /:/g,
            '-',
        )}`;
    }, [name, resource, reactId]);

    const getPlainTextFromEditorData = useCallback((editorData) => {
        if (!editorData?.blocks?.length) {
            return '';
        }

        return editorData.blocks
            .map((block) => {
                const data = block.data || {};

                switch (block.type) {
                    case 'header':
                    case 'paragraph':
                        return data.text || '';

                    case 'list':
                        return Array.isArray(data.items)
                            ? data.items.join('\n')
                            : '';

                    case 'quote':
                        return data.text || '';

                    default:
                        return '';
                }
            })
            .filter(Boolean)
            .join('\n\n')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<[^>]*>/g, '')
            .trim();
    }, []);

    const handleEditorChange = useCallback(
        (editorData) => {
            const sanitizedBlocks = (editorData?.blocks || []).filter(
                (block) => {
                    if (block?.type !== 'image') {
                        return true;
                    }

                    const path = block?.data?.path;
                    return typeof path === 'string' && path.trim().length > 0;
                },
            );

            const sanitizedEditorData = decodeArticleOutput({
                ...editorData,
                blocks: sanitizedBlocks,
            });

            const plainText = getPlainTextFromEditorData(sanitizedEditorData);

            if (sanitizedEditorData.blocks.length > 0) {
                onChange({
                    [name]: sanitizedEditorData,
                    ...(fallbackName ? { [fallbackName]: plainText } : {}),
                });

                return;
            }

            onChange({
                [name]: null,
                ...(fallbackName ? { [fallbackName]: '' } : {}),
            });
        },
        [fallbackName, getPlainTextFromEditorData, name, onChange],
    );

    const editorData = useMemo(() => {
        if (entity?.[name]) {
            return entity[name];
        }

        if (!fallbackName || !entity?.[fallbackName]) {
            return null;
        }

        return {
            time: 0,
            blocks: entity[fallbackName]
                .split(/\n{2,}/)
                .map((text) => text.trim())
                .filter(Boolean)
                .map((text) => ({
                    type: 'paragraph',
                    data: {
                        text: text.replace(/\n/g, '<br>'),
                    },
                })),
        };
    }, [entity, name, fallbackName]);

    const editorTools = useMemo(() => {
        const inlineToolbar = ['bold', 'italic', 'link', 'underline'];

        const baseTools = {
            header: {
                class: Header,
                config: {
                    placeholder: t('editor.headerPlaceholder'),
                    levels: [2, 3],
                    defaultLevel: 2,
                },
                inlineToolbar,
            },
            link: {
                class: InlineLink,
                inlineToolbar,
            },
            list: {
                class: List,
                inlineToolbar,
            },
            paragraph: {
                inlineToolbar,
            },
            socialEmbed: {
                class: SocialEmbed,
                data: { url: '' },
            },
            underline: Underline,
        };

        if (!fullFeatureSet) {
            return baseTools;
        }

        return {
            ...baseTools,
            quote: {
                class: Quote,
                config: {
                    quotePlaceholder: t('editor.Quote'),
                    captionPlaceholder: t('editor.Author'),
                },
            },
            image: {
                class: ImageTool,
                inlineToolbar,
                config: { resource },
            },
        };
    }, [fullFeatureSet, resource, t]);

    const editorI18n = useMemo(
        () => ({
            messages: translation.messages,
        }),
        [],
    );

    useEditorJs({
        holderId,
        data: editorData,
        tools: editorTools,
        placeholder: t('editor.placeholder'),
        i18n: editorI18n,
        onChange: handleEditorChange,
    });

    const hasFieldError = Object.hasOwnProperty.call(errors, name);

    return (
        <div className={formStyle.formGroup}>
            <Field {...fieldProps} name={name} resource={resource} unlabeled>
                {(hasError, attributes, isChanged) => (
                    <>
                        <Label
                            className={formStyle.label}
                            to={holderId}
                            text={t(`${resource}.${name}`)}
                        />
                        <div
                            className={`
                                ${style.editorWrapper}
                                ${formStyle.fieldInput}
                                ${isChanged ? formStyle.changedInput : ''}
                            `}
                        >
                            <div id={holderId} />
                            {hasFieldError && (
                                <Error message={errors[name][0]} />
                            )}
                        </div>
                    </>
                )}
            </Field>
        </div>
    );
};

BlockStyledEditor.propTypes = {
    entity: PropTypes.shape({
        id: PropTypes.number,
    }).isRequired,
    name: PropTypes.string.isRequired,
    fallbackName: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    resource: PropTypes.string.isRequired,
    errors: PropTypes.object.isRequired,
    fullFeatureSet: PropTypes.bool,
};

BlockStyledEditor.defaultProps = {
    fullFeatureSet: false,
    fallbackName: null,
};

export default BlockStyledEditor;
